import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  CircularProgress,
  Snackbar,
  Alert,
  IconButton,
  Chip,
  Drawer,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Grid,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  FactCheck as FactCheckIcon,
  RestartAlt as RestartAltIcon,
  Visibility as VisibilityIcon,
  TaskAlt as TaskAltIcon,
  Undo as UndoIcon,
} from '@mui/icons-material';
import {
  listRoomTypeApprovalRequests,
  approveRoomTypeApprovalRequest,
  rejectRoomTypeApprovalRequest,
  resetRoomTypeApprovalRequestToPending,
  setRoomTypeRequestAdminMarkedCreated,
} from '../api/adminPanelApi';

function normDetails(raw) {
  if (raw == null) return null;
  if (typeof raw === 'object') return raw;
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
  return null;
}

/** Prefer decoded room_add_details; fall back to payload_json for readability */
function rowAddDetails(row) {
  if (!row) return null;
  return normDetails(row.room_add_details) || normDetails(row.payload_json);
}

/** Normalize admin_marked_created; legacy room_created treated as old API */
function adminMarkedCreated(row) {
  if (!row) return 'no';
  const v = row.admin_marked_created ?? row.room_created;
  if (v == null || v === '') return 'no';
  const s = String(v).toLowerCase();
  if (s === 'yes' || s === 'y' || s === '1') return 'yes';
  return 'no';
}

function statusChipColor(st) {
  const s = (st || '').toLowerCase();
  if (s === 'pending') return 'warning';
  if (s === 'approved') return 'success';
  if (s === 'rejected') return 'error';
  if (s === 'failed') return 'error';
  return 'default';
}

function formatDateRange(detail) {
  if (!detail) return '–';
  const a = detail.start_date;
  const b = detail.end_date;
  if (a && b) return `${a} → ${b}`;
  if (a) return String(a);
  return '–';
}

function RateTable({ rate }) {
  if (rate == null || typeof rate !== 'object') {
    return (
      <Typography variant="body2" color="text.secondary">
        –
      </Typography>
    );
  }
  const entries = Object.entries(rate);
  if (entries.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        –
      </Typography>
    );
  }
  return (
    <Table size="small" sx={{ maxWidth: 400 }}>
      <TableHead>
        <TableRow>
          <TableCell>Key</TableCell>
          <TableCell align="right">Amount</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {entries.map(([k, v]) => (
          <TableRow key={k}>
            <TableCell>{k}</TableCell>
            <TableCell align="right">{v == null ? '–' : String(v)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function DetailField({ label, value }) {
  const display =
    value === null || value === undefined || value === ''
      ? '–'
      : typeof value === 'object'
        ? JSON.stringify(value, null, 2)
        : String(value);
  return (
    <Grid item xs={12} sm={6}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {display}
      </Typography>
    </Grid>
  );
}

const RoomTypeApprovals = () => {
  const navigate = useNavigate();
  /** '' = omit (all), 'no' | 'yes' = filter — default inbox = unmarked */
  const [adminMarkedFilter, setAdminMarkedFilter] = useState('no');
  const [hotelIdFilter, setHotelIdFilter] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [selected, setSelected] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [actionRowId, setActionRowId] = useState(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectTargetId, setRejectTargetId] = useState(null);
  const [rowActionValue, setRowActionValue] = useState({});

  const load = useCallback(
    async (opts = {}) => {
      const { silent } = opts;
      setLoading(true);
      try {
        const filters = {};
        if (hotelIdFilter.trim()) filters.hotel_id = hotelIdFilter.trim();
        if (adminMarkedFilter !== '') filters.admin_marked_created = adminMarkedFilter;
        const res = await listRoomTypeApprovalRequests(filters);
        if (res.success) {
          const newRows = res.rows || [];
          setRows(newRows);
          setSelected((prev) => {
            if (!prev?.id) return prev;
            const match = newRows.find((r) => String(r.id) === String(prev.id));
            return match ?? prev;
          });
          if (!silent) {
            setSnackbar({
              open: true,
              message: res.message || `Loaded ${newRows.length} row(s)`,
              severity: 'success',
            });
          }
        } else {
          setRows([]);
          setSnackbar({ open: true, message: res.error || 'Load failed', severity: 'error' });
        }
      } catch (e) {
        setRows([]);
        setSnackbar({ open: true, message: e.message || 'Load failed', severity: 'error' });
      } finally {
        setLoading(false);
      }
    },
    [hotelIdFilter, adminMarkedFilter]
  );

  useEffect(() => {
    load({ silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openDetails = (row) => {
    setSelected(row);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
  };

  const showSnack = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });

  const handleMarkReviewed = async (id) => {
    setActionRowId(id);
    try {
      const res = await setRoomTypeRequestAdminMarkedCreated(id, 'yes');
      if (res.success) {
        showSnack(res.data?.message || 'Marked reviewed');
        await load({ silent: true });
      } else {
        showSnack(res.error || 'Could not mark reviewed', 'error');
      }
    } catch (e) {
      showSnack(e.message || 'Could not mark reviewed', 'error');
    } finally {
      setActionRowId(null);
    }
  };

  const handleUnmarkReviewed = async (id) => {
    setActionRowId(id);
    try {
      const res = await setRoomTypeRequestAdminMarkedCreated(id, 'no');
      if (res.success) {
        showSnack(res.data?.message || 'Returned to inbox');
        await load({ silent: true });
      } else {
        showSnack(res.error || 'Could not unmark', 'error');
      }
    } catch (e) {
      showSnack(e.message || 'Could not unmark', 'error');
    } finally {
      setActionRowId(null);
    }
  };

  /** Legacy: close log when room already exists (pending / failed only) */
  const handleLegacyCloseLog = async (id) => {
    setActionRowId(id);
    try {
      const res = await approveRoomTypeApprovalRequest(id);
      if (res.success) {
        showSnack(res.data?.message || 'Log closed (acknowledged)');
        await load({ silent: true });
        setDrawerOpen(false);
      } else {
        const hint =
          res.error ||
          'No matching roomtype row yet — reject this log or have the host add the room from the dashboard.';
        showSnack(hint, 'error');
      }
    } catch (e) {
      showSnack(e.message || 'Could not close log', 'error');
    } finally {
      setActionRowId(null);
    }
  };

  const openReject = (id) => {
    setRejectTargetId(id);
    setRejectReason('');
    setRejectOpen(true);
  };

  const confirmReject = async () => {
    if (rejectTargetId == null) return;
    setActionRowId(rejectTargetId);
    setRejectOpen(false);
    try {
      const res = await rejectRoomTypeApprovalRequest(rejectTargetId, rejectReason);
      if (res.success) {
        showSnack(res.data?.message || 'Rejected');
        await load({ silent: true });
        setDrawerOpen(false);
      } else {
        showSnack(res.error || 'Reject failed', 'error');
      }
    } catch (e) {
      showSnack(e.message || 'Reject failed', 'error');
    } finally {
      setActionRowId(null);
      setRejectTargetId(null);
    }
  };

  const handleReset = async (id) => {
    setActionRowId(id);
    try {
      const res = await resetRoomTypeApprovalRequestToPending(id);
      if (res.success) {
        showSnack(res.data?.message || 'Reset to pending');
        await load({ silent: true });
      } else {
        showSnack(res.error || 'Reset failed', 'error');
      }
    } catch (e) {
      showSnack(e.message || 'Reset failed', 'error');
    } finally {
      setActionRowId(null);
    }
  };

  const detail = selected ? rowAddDetails(selected) : null;
  const st = (selected?.status || '').toLowerCase();
  const marked = selected ? adminMarkedCreated(selected) : 'no';

  const showLegacyCloseLog = (row) => {
    if (!row) return false;
    const s = (row.status || '').toLowerCase();
    return s === 'pending' || s === 'failed';
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1600, mx: 'auto' }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <IconButton onClick={() => navigate('/')} sx={{ color: '#FF7700' }}>
          <ArrowBackIcon />
        </IconButton>
        <FactCheckIcon sx={{ color: '#2E4057', fontSize: 36 }} />
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Typography variant="h4" fontWeight={700} sx={{ color: '#2E4057' }}>
            Room type add — ops log
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Host direct-add writes MySQL immediately; this table is for visibility and review. Use <strong>Mark reviewed</strong>{' '}
            to clear items from the inbox. Legacy <strong>Close log</strong> only acknowledges pending/failed rows when a
            roomtype row already exists — it does not create rooms.
          </Typography>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-end' }}>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Admin marked</InputLabel>
            <Select
              label="Admin marked"
              value={adminMarkedFilter}
              onChange={(e) => setAdminMarkedFilter(e.target.value)}
            >
              <MenuItem value="">All (admin marked)</MenuItem>
              <MenuItem value="no">No — needs review</MenuItem>
              <MenuItem value="yes">Yes — reviewed</MenuItem>
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="Hotel ID (optional)"
            value={hotelIdFilter}
            onChange={(e) => setHotelIdFilter(e.target.value)}
            sx={{ width: 200 }}
          />
          <Button
            variant="outlined"
            size="small"
            onClick={() => setAdminMarkedFilter('no')}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Unmarked only
          </Button>
          <Button
            variant="contained"
            onClick={load}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />}
            sx={{ backgroundColor: '#FF7700', '&:hover': { backgroundColor: '#E65100' } }}
          >
            Refresh
          </Button>
        </Box>
      </Paper>

      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Hotel</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Admin marked</TableCell>
              <TableCell>Dates</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={8}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                    No rows. Adjust admin-marked filter or hotel ID and refresh.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => {
                const d = rowAddDetails(row);
                const rowSt = (row.status || '').toLowerCase();
                const busy = actionRowId === row.id;
                const marked = adminMarkedCreated(row);
                const legacyClose = showLegacyCloseLog(row);

                return (
                  <TableRow key={row.id} hover sx={{ cursor: 'pointer' }} onClick={() => openDetails(row)}>
                    <TableCell onClick={(e) => e.stopPropagation()}>{row.id}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {row.hotel_name || '–'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {row.hotel_id}
                      </Typography>
                    </TableCell>
                    <TableCell>{row.category_name || row.room_type_category_id || '–'}</TableCell>
                    <TableCell>
                      <Chip size="small" label={row.status || '–'} color={statusChipColor(row.status)} />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={marked === 'yes' ? 'yes' : 'no'}
                        color={marked === 'yes' ? 'success' : 'warning'}
                      />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateRange(d)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.created_at || '–'}</TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                          gap: 0.75,
                          flexWrap: 'wrap',
                        }}
                      >
                        <IconButton
                          size="medium"
                          onClick={() => openDetails(row)}
                          aria-label="details"
                          sx={{ color: 'action.active' }}
                        >
                          <VisibilityIcon sx={{ fontSize: 30 }} />
                        </IconButton>
                        {marked === 'no' && (
                          <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            disabled={busy}
                            onClick={() => handleMarkReviewed(row.id)}
                            startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <TaskAltIcon />}
                            sx={{ textTransform: 'none', fontWeight: 600, minWidth: 0, px: 1.25 }}
                          >
                            Mark reviewed
                          </Button>
                        )}
                        {marked === 'yes' && (
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={busy}
                            onClick={() => handleUnmarkReviewed(row.id)}
                            startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <UndoIcon />}
                            sx={{ textTransform: 'none', fontWeight: 600, minWidth: 0, px: 1.25 }}
                          >
                            Unmark
                          </Button>
                        )}
                        {rowSt === 'pending' && (
                          <FormControl size="small" sx={{ minWidth: 200 }} disabled={busy}>
                            <InputLabel id={`rta-pending-${row.id}`}>More</InputLabel>
                            <Select
                              labelId={`rta-pending-${row.id}`}
                              label="More"
                              value={rowActionValue[row.id] ?? 'none'}
                              onChange={(e) => {
                                const v = e.target.value;
                                const rid = row.id;
                                if (v === 'none') return;
                                setRowActionValue((p) => ({ ...p, [rid]: 'none' }));
                                if (v === 'closeLog' && legacyClose) void handleLegacyCloseLog(rid);
                                else if (v === 'reject') openReject(rid);
                              }}
                              renderValue={(v) =>
                                v === 'none' ? (
                                  <Typography variant="body2" color="text.secondary" component="span">
                                    More…
                                  </Typography>
                                ) : v === 'closeLog' ? (
                                  'Close log…'
                                ) : (
                                  'Reject'
                                )
                              }
                            >
                              <MenuItem value="none" disabled>
                                <em>Choose…</em>
                              </MenuItem>
                              {legacyClose && (
                                <MenuItem value="closeLog">Close log (room already exists)</MenuItem>
                              )}
                              <MenuItem value="reject">Reject</MenuItem>
                            </Select>
                          </FormControl>
                        )}
                        {rowSt === 'failed' && (
                          <FormControl size="small" sx={{ minWidth: 220 }} disabled={busy}>
                            <InputLabel id={`rta-failed-${row.id}`}>More</InputLabel>
                            <Select
                              labelId={`rta-failed-${row.id}`}
                              label="More"
                              value={rowActionValue[row.id] ?? 'none'}
                              onChange={(e) => {
                                const v = e.target.value;
                                const rid = row.id;
                                if (v === 'none') return;
                                setRowActionValue((p) => ({ ...p, [rid]: 'none' }));
                                if (v === 'reset') void handleReset(rid);
                                else if (v === 'closeLog' && legacyClose) void handleLegacyCloseLog(rid);
                              }}
                              renderValue={(v) =>
                                v === 'none' ? (
                                  <Typography variant="body2" color="text.secondary" component="span">
                                    More…
                                  </Typography>
                                ) : v === 'reset' ? (
                                  'Reset to pending'
                                ) : (
                                  'Close log…'
                                )
                              }
                            >
                              <MenuItem value="none" disabled>
                                <em>Choose…</em>
                              </MenuItem>
                              <MenuItem value="reset">Reset to pending</MenuItem>
                              {legacyClose && (
                                <MenuItem value="closeLog">Close log (room already exists)</MenuItem>
                              )}
                            </Select>
                          </FormControl>
                        )}
                        {busy && <CircularProgress size={22} sx={{ flexShrink: 0 }} />}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Drawer anchor="right" open={drawerOpen} onClose={closeDrawer} PaperProps={{ sx: { width: { xs: '100%', sm: 440, md: 560 }, p: 2 } }}>
        {selected && (
          <Box>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
              Log #{selected.id}
            </Typography>
            <Chip size="small" label={selected.status} color={statusChipColor(selected.status)} sx={{ mr: 1 }} />
            <Chip
              size="small"
              variant="outlined"
              label={`Admin marked: ${marked}`}
              color={marked === 'yes' ? 'success' : 'warning'}
              sx={{ mb: 1 }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {selected.hotel_name} · Hotel ID {selected.hotel_id}
            </Typography>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Category: {selected.category_name || selected.room_type_category_id || '–'}
            </Typography>

            <Alert severity="info" sx={{ mb: 2 }}>
              Direct-add from the host dashboard creates the room in MySQL immediately. This row is an ops log — use{' '}
              <strong>Mark reviewed</strong> when you have finished looking. Legacy <strong>Close log</strong> is only for
              pending/failed queues when a roomtype already exists.
            </Alert>

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
              Room add details
            </Typography>
            {detail ? (
              <Grid container spacing={1.5}>
                <DetailField label="Hotel ID" value={detail.hotel_id} />
                <DetailField label="Room type ID" value={detail.room_type_id} />
                <DetailField label="Room count" value={detail.room_count} />
                <DetailField label="Occupancy" value={detail.occupancy} />
                <DetailField label="Amenities" value={detail.amenities} />
                <DetailField label="Start date" value={detail.start_date} />
                <DetailField label="End date" value={detail.end_date} />
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Rate map
                  </Typography>
                  <RateTable rate={detail.rate} />
                </Grid>
              </Grid>
            ) : (
              <Alert severity="warning">No decoded details — see raw payload_json below.</Alert>
            )}

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>
              Log meta
            </Typography>
            <Typography variant="body2" component="div" sx={{ mb: 0.75 }}>
              <strong>Created:</strong> {selected.created_at ?? '–'}
            </Typography>
            <Typography variant="body2" component="div" sx={{ mb: 0.75 }}>
              <strong>Updated:</strong> {selected.updated_at ?? '–'}
            </Typography>
            <Typography variant="body2" component="div" sx={{ mb: 0.75 }}>
              <strong>Reviewed at:</strong> {selected.reviewed_at ?? '–'}
            </Typography>
            <Typography variant="body2" component="div" sx={{ mb: 0.75 }}>
              <strong>Reviewed by:</strong> {selected.reviewed_by ?? '–'}
            </Typography>
            <Typography variant="body2" component="div" sx={{ mb: 1 }}>
              <strong>Admin marked created:</strong> {selected.admin_marked_created ?? selected.room_created ?? '–'}
            </Typography>
            {(st === 'failed' || st === 'rejected') && (
              <Alert severity={st === 'failed' ? 'error' : 'warning'} sx={{ mt: 1, mb: 1 }}>
                <Typography variant="subtitle2" fontWeight={700}>
                  last_error
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {selected.last_error == null || selected.last_error === '' ? '(empty)' : String(selected.last_error)}
                </Typography>
              </Alert>
            )}

            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              payload_json (raw)
            </Typography>
            <Paper variant="outlined" sx={{ p: 1, maxHeight: 200, overflow: 'auto', fontFamily: 'monospace', fontSize: 11 }}>
              {selected.payload_json || '–'}
            </Paper>

            <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              {marked === 'no' && (
                <Button
                  variant="contained"
                  disabled={actionRowId === selected.id}
                  startIcon={actionRowId === selected.id ? <CircularProgress size={18} color="inherit" /> : <TaskAltIcon />}
                  onClick={() => handleMarkReviewed(selected.id)}
                  sx={{ backgroundColor: '#2E4057', '&:hover': { backgroundColor: '#1a252f' } }}
                >
                  Mark reviewed
                </Button>
              )}
              {marked === 'yes' && (
                <Button
                  variant="outlined"
                  disabled={actionRowId === selected.id}
                  startIcon={actionRowId === selected.id ? <CircularProgress size={18} color="inherit" /> : <UndoIcon />}
                  onClick={() => handleUnmarkReviewed(selected.id)}
                >
                  Unmark (back to inbox)
                </Button>
              )}
              {st === 'pending' && (
                <>
                  {showLegacyCloseLog(selected) && (
                    <Button
                      variant="outlined"
                      disabled={actionRowId === selected.id}
                      onClick={() => handleLegacyCloseLog(selected.id)}
                      sx={{ textTransform: 'none' }}
                    >
                      Close log (room already exists)
                    </Button>
                  )}
                  <Button variant="outlined" color="error" disabled={actionRowId === selected.id} onClick={() => openReject(selected.id)}>
                    Reject
                  </Button>
                </>
              )}
              {st === 'failed' && (
                <>
                  <Button
                    variant="contained"
                    color="warning"
                    disabled={actionRowId === selected.id}
                    startIcon={actionRowId === selected.id ? <CircularProgress size={18} color="inherit" /> : <RestartAltIcon />}
                    onClick={() => handleReset(selected.id)}
                  >
                    Reset to pending
                  </Button>
                  {showLegacyCloseLog(selected) && (
                    <Button variant="outlined" disabled={actionRowId === selected.id} onClick={() => handleLegacyCloseLog(selected.id)} sx={{ textTransform: 'none' }}>
                      Close log (room already exists)
                    </Button>
                  )}
                </>
              )}
            </Box>
          </Box>
        )}
      </Drawer>

      <Dialog open={rejectOpen} onClose={() => !actionRowId && setRejectOpen(false)}>
        <DialogTitle>Reject log</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>Optional reason is sent to the API (pending rows only).</DialogContentText>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            label="Reason (optional)"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectOpen(false)}>Cancel</Button>
          <Button onClick={confirmReject} color="error" variant="contained" disabled={actionRowId != null}>
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RoomTypeApprovals;
